import { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import OneSignal from "@onesignal/capacitor-plugin";

const ONE_SIGNAL_APP_ID = "6904d7a3-b8cf-4f90-89e5-c9196008f75c";

/** Set once the preprompt should never be shown again (permission granted / explicit request). */
const PREPROMPT_KEY = "sc:push-preprompt-answered";
/** Timestamp (ms) until which the preprompt is deferred after a "Not now". */
const DEFER_KEY = "sc:push-preprompt-deferred-until";

/** How long "Not now" defers the explainer. */
const DEFER_MS = 7 * 24 * 60 * 60 * 1000;

/** Deferred for the remainder of this app session (never persisted). */
let deferredThisSession = false;

let initialized = false;
let initPromise: Promise<boolean> | null = null;

export type PushPermissionState =
  | "unsupported"
  | "unknown"
  | "notDetermined"
  | "granted"
  | "denied";

export const isNativePush = (): boolean => Capacitor.isNativePlatform();

/**
 * Initialize OneSignal on native launch.
 *
 * IMPORTANT: this intentionally does NOT request the iOS system permission.
 * Permission is requested contextually from product UI via
 * `requestPushPermission()` so the user understands the value first.
 */
async function initOneSignal(): Promise<boolean> {
  if (!isNativePush()) return false;
  if (initialized) return true;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      await OneSignal.initialize(ONE_SIGNAL_APP_ID);
      initialized = true;
      return true;
    } catch (err) {
      console.error("[OneSignal] Initialization error:", err);
      return false;
    }
  })();

  return initPromise;
}

export function useOneSignal() {
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void initOneSignal();
  }, []);
}

/**
 * Best-effort native permission state.
 *
 * Uses the OS-level status when the plugin exposes it (iOS
 * OSNotificationPermission: 0 not determined, 1 denied, 2 authorized,
 * 3 provisional, 4 ephemeral) and falls back to the boolean API.
 */
export async function getPushPermissionState(): Promise<PushPermissionState> {
  if (!isNativePush()) return "unsupported";
  try {
    await initOneSignal();
    const api = OneSignal.Notifications as unknown as {
      getPermissionAsync?: () => Promise<boolean>;
      permissionNative?: () => Promise<number>;
    };

    if (api.permissionNative) {
      try {
        const native = await api.permissionNative();
        if (typeof native === "number") {
          if (native === 0) return "notDetermined";
          if (native === 1) return "denied";
          return "granted";
        }
      } catch {
        /* fall through to the boolean API */
      }
    }

    if (api.getPermissionAsync) {
      const granted = await api.getPermissionAsync();
      // A false boolean can't distinguish "denied" from "not determined".
      return granted ? "granted" : "unknown";
    }
    return "unknown";
  } catch {
    return "unknown";
  }
}

/** Legacy boolean helper: true granted, false not granted, null on web/unknown. */
export async function getPushPermission(): Promise<boolean | null> {
  const state = await getPushPermissionState();
  if (state === "granted") return true;
  if (state === "denied" || state === "notDetermined") return false;
  return null;
}

export const hasAnsweredPushPrePrompt = (): boolean => {
  try {
    return localStorage.getItem(PREPROMPT_KEY) === "1";
  } catch {
    return false;
  }
};

export const markPushPrePromptAnswered = (): void => {
  try {
    localStorage.setItem(PREPROMPT_KEY, "1");
  } catch {
    /* storage unavailable — prompt simply may reappear next launch */
  }
};

/** "Not now": defer for this session and for a modest cooldown on this device. */
export const deferPushPrePrompt = (): void => {
  deferredThisSession = true;
  try {
    localStorage.setItem(DEFER_KEY, String(Date.now() + DEFER_MS));
  } catch {
    /* session-only deferral still applies */
  }
};

const isDeferralActive = (): boolean => {
  if (deferredThisSession) return true;
  try {
    const raw = localStorage.getItem(DEFER_KEY);
    if (!raw) return false;
    const until = Number(raw);
    return Number.isFinite(until) && Date.now() < until;
  } catch {
    return false;
  }
};

/**
 * Ask iOS for notification permission. Safe to call on web (resolves false).
 * Never throws.
 */
export async function requestPushPermission(): Promise<boolean> {
  markPushPrePromptAnswered();
  if (!isNativePush()) return false;
  try {
    const ready = await initOneSignal();
    if (!ready) return false;
    const granted = await OneSignal.Notifications.requestPermission(true);
    return !!granted;
  } catch (err) {
    console.error("[OneSignal] Permission request failed:", err);
    return false;
  }
}

/**
 * Should we show our own explainer before the system prompt?
 * False on web, when granted or explicitly denied, after a permanent answer,
 * and while a "Not now" deferral (session or cooldown) is active.
 */
export async function shouldShowPushPrePrompt(): Promise<boolean> {
  if (!isNativePush()) return false;
  if (hasAnsweredPushPrePrompt()) return false;
  if (isDeferralActive()) return false;
  const state = await getPushPermissionState();
  return state === "notDetermined" || state === "unknown";
}

/** Fired by product surfaces after a meaningful action (e.g. following an artist). */
export const PUSH_PROMPT_EVENT = "soundcheck:push-prompt";

export function maybePromptForPush(): void {
  if (typeof window === "undefined") return;
  void shouldShowPushPrePrompt().then((show) => {
    if (show) window.dispatchEvent(new CustomEvent(PUSH_PROMPT_EVENT));
  });
}

/** Register a native notification-click handler. Returns a cleanup function. */
export function addPushClickListener(
  handler: (data: Record<string, any> | null) => void,
): () => void {
  if (!isNativePush()) return () => {};
  let disposed = false;
  const listener = (event: any) => {
    if (disposed) return;
    const data =
      event?.notification?.additionalData ??
      event?.notification?.rawPayload?.custom?.a ??
      null;
    handler(data ?? null);
  };
  void initOneSignal().then((ready) => {
    if (!ready || disposed) return;
    try {
      (OneSignal.Notifications as any).addEventListener("click", listener);
    } catch (err) {
      console.error("[OneSignal] Could not attach click listener:", err);
    }
  });
  return () => {
    disposed = true;
    try {
      (OneSignal.Notifications as any).removeEventListener?.("click", listener);
    } catch {
      /* no-op */
    }
  };
}

/** UI-friendly permission state for settings surfaces. */
export function usePushPermissionState() {
  const [supported] = useState(() => isNativePush());
  const [state, setState] = useState<PushPermissionState>(() =>
    isNativePush() ? "unknown" : "unsupported",
  );
  const [requesting, setRequesting] = useState(false);

  const refresh = useCallback(async () => {
    setState(await getPushPermissionState());
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const request = useCallback(async () => {
    setRequesting(true);
    const ok = await requestPushPermission();
    setRequesting(false);
    setState(ok ? "granted" : await getPushPermissionState());
    return ok;
  }, []);

  const granted = state === "granted" ? true : state === "unsupported" ? null : false;

  return { supported, state, granted, denied: state === "denied", requesting, request, refresh };
}
